import React, { useRef, useEffect, useState, ReactNode } from 'react';
import breakpoints from 'theme/atoms/breakpoints';
import { ECoachSection, UserSection } from './ChatArea.style';

interface ResponsiveECoachSectionProps {
  children: ReactNode;
  hidden?: boolean;
}

interface ResponsiveUserSectionProps {
  children: ReactNode;
}

// Function to determine padding size based on width
const getPaddingSizeFromWidth = (width: number): 'large' | 'none' => {
  if (width < parseInt(breakpoints.tabletMin, 10)) {
    return 'none';
  }
  return 'large';
};

// Responsive component that measures its own width and sets padding accordingly
export const ResponsiveECoachSection: React.FC<
  ResponsiveECoachSectionProps
> = ({ children, hidden }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paddingSize, setPaddingSize] = useState<'large' | 'none'>('large');

  // Set up resize observer to measure width and update padding
  useEffect(() => {
    if (!containerRef.current) return;

    const updatePadding = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setPaddingSize(getPaddingSizeFromWidth(width));
      }
    };

    // Initialize padding
    updatePadding();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updatePadding);
    resizeObserver.observe(containerRef.current);

    // When cleanup is needed
    // eslint-disable-next-line
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <ECoachSection ref={containerRef} hidden={hidden} paddingSize={paddingSize}>
      {children}
    </ECoachSection>
  );
};

// Responsive component for user section
export const ResponsiveUserSection: React.FC<ResponsiveUserSectionProps> = ({
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paddingSize, setPaddingSize] = useState<'large' | 'none'>('large');

  // Set up resize observer to measure width and update padding
  useEffect(() => {
    if (!containerRef.current) return;

    const updatePadding = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setPaddingSize(getPaddingSizeFromWidth(width));
      }
    };

    // Initialize padding
    updatePadding();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updatePadding);
    resizeObserver.observe(containerRef.current);

    // When cleanup is needed
    // eslint-disable-next-line
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <UserSection ref={containerRef} paddingSize={paddingSize}>
      {children}
    </UserSection>
  );
};
